// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import type {UserPartialResponse} from '@fluxer/schema/src/domains/user/UserResponseSchemas';
import {makeAutoObservable} from 'mobx';

export interface FeedPostPreview {
	author: UserPartialResponse;
	text: string;
	imageUrl: string | null;
}

export type FeedPostPreviewSlot = {status: 'loading'} | {status: 'ready'; preview: FeedPostPreview} | {status: 'none'};

const MAX_SLOTS = 500;

// Micup: the posts linked in messages, asked once each per session; a post the server refuses stays 'none'. At most
// MAX_SLOTS are kept: the oldest settled one goes first and is asked again if it shows up later; a load in flight stays.
class FeedPostPreviews {
	private slots = new Map<string, FeedPostPreviewSlot>();
	private session = 0;

	constructor() {
		makeAutoObservable<FeedPostPreviews, 'session'>(this, {session: false}, {autoBind: true});
	}

	get(postId: string): FeedPostPreviewSlot | null {
		return this.slots.get(postId) ?? null;
	}

	// The session the request belongs to, or null when this post was already asked for.
	beginLoad(postId: string): number | null {
		if (this.slots.has(postId)) return null;
		this.slots.set(postId, {status: 'loading'});
		this.evict();
		return this.session;
	}

	handleLoaded(session: number, postId: string, preview: FeedPostPreview | null): void {
		if (session !== this.session) return;
		this.slots.delete(postId);
		this.slots.set(postId, preview ? {status: 'ready', preview} : {status: 'none'});
		this.evict();
	}

	private evict(): void {
		for (const [postId, slot] of this.slots) {
			if (this.slots.size <= MAX_SLOTS) return;
			if (slot.status !== 'loading') this.slots.delete(postId);
		}
	}

	reset(): void {
		this.session++;
		this.slots.clear();
	}
}

const feedPostPreviews = new FeedPostPreviews();
ResettableStates.register(feedPostPreviews, feedPostPreviews.reset);
AccountScopedWork.registerCancellation(() => feedPostPreviews.reset());

export default feedPostPreviews;
